// src/components/chat/PromptCoachPanel.tsx

import * as React from "react";
import { parsePromptCoach } from "../../utils/promptCoachParser";
import { ClickablePrompt, PromptCoachData } from "../../interfaces/promptCoachTypes";
import { createLogger } from "../../utils/logger";

const log = createLogger("PromptCoachPanel");

/**
 * SVG Icons for Prompt Coach tabs
 * Each icon accepts a color prop to dynamically change stroke color
 */
const NextStepsIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 20,
  color = "#6B7280",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_34_101)">
      <path
        d="M8.28086 12.9167C8.20647 12.6283 8.05615 12.3651 7.84555 12.1545C7.63494 11.9439 7.37176 11.7936 7.08336 11.7192L1.97086 10.4009C1.88364 10.3761 1.80687 10.3236 1.75221 10.2512C1.69754 10.1789 1.66797 10.0907 1.66797 10C1.66797 9.90938 1.69754 9.82118 1.75221 9.74884C1.80687 9.6765 1.88364 9.62397 1.97086 9.59921L7.08336 8.28005C7.37166 8.20572 7.63477 8.05552 7.84537 7.84508C8.05596 7.63463 8.20634 7.37162 8.28086 7.08338L9.5992 1.97088C9.6237 1.88331 9.67618 1.80616 9.74863 1.75121C9.82108 1.69625 9.90951 1.6665 10.0004 1.6665C10.0914 1.6665 10.1798 1.69625 10.2523 1.75121C10.3247 1.80616 10.3772 1.88331 10.4017 1.97088L11.7192 7.08338C11.7936 7.37177 11.9439 7.63496 12.1545 7.84556C12.3651 8.05616 12.6283 8.20648 12.9167 8.28088L18.0292 9.59838C18.1171 9.62263 18.1946 9.67505 18.2499 9.74761C18.3052 9.82017 18.3351 9.90885 18.3351 10C18.3351 10.0912 18.3052 10.1799 18.2499 10.2525C18.1946 10.325 18.1171 10.3775 18.0292 10.4017L12.9167 11.7192C12.6283 11.7936 12.3651 11.9439 12.1545 12.1545C11.9439 12.3651 11.7936 12.6283 11.7192 12.9167L10.4009 18.0292C10.3764 18.1168 10.3239 18.1939 10.2514 18.2489C10.179 18.3038 10.0905 18.3336 9.99961 18.3336C9.90868 18.3336 9.82025 18.3038 9.7478 18.2489C9.67535 18.1939 9.62287 18.1168 9.59836 18.0292L8.28086 12.9167Z"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16.6667 2.5V5.83333"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.3333 4.16675H15"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.33325 14.1667V15.8334"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.16667 15H2.5"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_34_101">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const StrategicQuestionIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = "#6B7280",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 9.33325C10.1333 8.66659 10.4667 8.19992 11 7.66659C11.6667 7.06659 12 6.19992 12 5.33325C12 4.27239 11.5786 3.25497 10.8284 2.50482C10.0783 1.75468 9.06087 1.33325 8 1.33325C6.93913 1.33325 5.92172 1.75468 5.17157 2.50482C4.42143 3.25497 4 4.27239 4 5.33325C4 5.99992 4.13333 6.79992 5 7.66659C5.46667 8.13325 5.86667 8.66659 6 9.33325"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 12H10"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.66675 14.6667H9.33341"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PromptEnhancementIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = "#6B7280",
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_34_103)">
      <path
        d="M10.6667 4.66675H14.6667V8.66675"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.6666 4.66675L8.99992 10.3334L5.66659 7.00008L1.33325 11.3334"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_34_103">
        <rect width="16" height="16" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

/**
 * Props interface for PromptCoachPanel component
 */
interface PromptCoachPanelProps {
  // Accept pre-parsed data (preferred) OR markdown content (backward compatibility)
  parsedData?: PromptCoachData | null; // Pre-parsed Prompt Coach data
  markdownContent?: string; // Raw Prompt Coach section markdown (legacy)
  onPromptClick: (promptText: string) => void; // Callback when user clicks a prompt
  className?: string;
  isStreaming?: boolean; // Whether the AI response is still being generated
  /**
   * A card the add-in adds after the model's next steps, for an action the
   * workflow always offers regardless of what the model suggested. Shown in
   * the Next Steps tab, and counted as content so the panel renders for it
   * even when the model's own section came back empty.
   */
  pinnedAction?: React.ReactNode;
}

/**
 * Tab configuration for the three Prompt Coach elements
 */
interface TabConfig {
  id: "nextSteps" | "strategic" | "enhancement";
  label: string;
  IconComponent: React.FC<{ size?: number; color?: string }>; // SVG icon component
  iconSize: number; // Icon size
  color: string; // Primary color (borders, backgrounds, gradients)
  textColor?: string; // Optional text color (defaults to color if not specified)
}

/**
 * PromptCoachPanel Component
 *
 * Renders Prompt Coach sections as an interactive, tab-based UI.
 * Parses markdown and displays three elements:
 * 1. Strategic Question (read-only, blue)
 * 2. Prompt Enhancement (read-only, green)
 * 3. Next Steps (clickable prompts, purple)
 *
 * Features:
 * - Tab-based navigation
 * - Clickable prompts populate chat input
 * - Graceful degradation on parse errors
 * - Keyboard accessible
 * - Responsive design (320px+)
 */
const PromptCoachPanel: React.FC<PromptCoachPanelProps> = ({
  parsedData,
  markdownContent,
  onPromptClick,
  className,
  isStreaming = false,
  pinnedAction,
}) => {
  // Use pre-parsed data if provided, otherwise parse markdown (legacy path)
  const data = React.useMemo(() => {
    // Preferred: Use pre-parsed data from parent
    if (parsedData) {
      return parsedData;
    }

    // Legacy: Parse markdown content if no parsed data provided
    if (markdownContent) {
      const parseResult = parsePromptCoach(markdownContent);
      if (parseResult.success && parseResult.data) {
        return parseResult.data;
      }
      // Parsing failed - return null to avoid rendering
      return null;
    }

    // No data provided at all
    return null;
  }, [parsedData, markdownContent]);

  // State for active tab
  const [activeTab, setActiveTab] = React.useState<string>("");
  const [hoveredPrompt, setHoveredPrompt] = React.useState<string | null>(null);
  const [hoveredTab, setHoveredTab] = React.useState<string | null>(null);

  // ALWAYS show all 3 tabs (with placeholders for missing content)
  const allTabs: TabConfig[] = [
    {
      id: "nextSteps",
      label: "Next Steps",
      IconComponent: NextStepsIcon,
      iconSize: 20,
      color: "#9333EA", // Purple
    },
    {
      id: "strategic",
      label: "Strategic Insight",
      IconComponent: StrategicQuestionIcon,
      iconSize: 16,
      color: "#0066cc", // Blue
    },
    {
      id: "enhancement",
      label: "Prompt Enhancement",
      IconComponent: PromptEnhancementIcon,
      iconSize: 16,
      color: "#00cc66", // Bright green (borders/backgrounds)
      textColor: "#059669", // Toned-down green (text/icons)
    },
  ];

  // Set default active tab (always first tab: Next Steps)
  // MUST be called before early returns to satisfy Rules of Hooks
  React.useEffect(() => {
    if (!activeTab) {
      setActiveTab(allTabs[0].id);
    }
  }, []);

  // If no valid data, allow rendering during streaming to show loading animations,
  // and for a pinned action, which needs the panel whatever the model produced
  let effectiveData = data;
  if (!effectiveData) {
    if (isStreaming || pinnedAction) {
      // Create empty placeholder data to trigger loading animations
      effectiveData = { rawSectionContent: "" };
    } else {
      // Not streaming and no data - don't render
      return null;
    }
  }

  // Check if we have ANY content across all 3 elements
  // During streaming, allow panel to render even without content (will show loading)
  const hasAnyContent =
    isStreaming ||
    !!pinnedAction ||
    (effectiveData.element1 && effectiveData.element1.content.trim().length > 0) ||
    (effectiveData.element2 && effectiveData.element2.content.trim().length > 0) ||
    (effectiveData.element3 && effectiveData.element3.clickablePrompts.length > 0);

  // If we found NO content and not streaming, don't show the panel
  if (!hasAnyContent) {
    return null;
  }

  // Handle prompt click
  const handlePromptClick = (prompt: ClickablePrompt) => {
    log.info("Prompt clicked", { promptId: prompt.id, text: prompt.text });
    onPromptClick(prompt.text);
  };

  // Handle keyboard navigation for tabs
  const handleTabKeyDown = (event: React.KeyboardEvent, tabId: string, tabIndex: number) => {
    if (event.key === "ArrowLeft" && tabIndex > 0) {
      setActiveTab(allTabs[tabIndex - 1].id);
    } else if (event.key === "ArrowRight" && tabIndex < allTabs.length - 1) {
      setActiveTab(allTabs[tabIndex + 1].id);
    } else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setActiveTab(tabId);
    }
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    width: "100%",
    backgroundColor: "#FFFFFF",
    marginTop: "0",
    marginBottom: "16px",
    fontFamily: "'Segoe UI', sans-serif",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "16px",
    fontWeight: 600,
    color: "#111827",
    marginBottom: "16px",
  };

  const tabListStyle: React.CSSProperties = {
    display: "flex",
    gap: "4px",
    marginBottom: "16px",
  };

  const getTabButtonStyle = (
    tab: TabConfig,
    isActive: boolean,
    isHovered: boolean
  ): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 18px",
    background: isActive ? "rgba(249, 250, 251, 0.6)" : "transparent",
    border: isActive ? `1px solid ${tab.color}40` : "1px solid transparent",
    borderRadius: "50px",
    color: isActive ? tab.color : "#6B7280",
    fontSize: "14px",
    fontWeight: isActive ? 600 : 500,
    cursor: "pointer",
    transition: "all 200ms ease-out",
    fontFamily: "'Segoe UI', sans-serif",
    outline: "none",
    position: "relative",
    overflow: "hidden",
    boxShadow: isActive
      ? `0 2px 6px ${tab.color}15`
      : isHovered
        ? "0 1px 3px rgba(0, 0, 0, 0.04)"
        : "none",
    transform: isActive || isHovered ? "translateY(-0.5px)" : "translateY(0)",
  });

  const contentAreaStyle: React.CSSProperties = {
    paddingTop: "8px",
  };

  const elementHeaderStyle = (color: string): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color,
    marginBottom: "12px",
    fontSize: "14px",
    fontWeight: 600,
  });

  const contentBoxStyle = (color: string, isItalic: boolean): React.CSSProperties => ({
    padding: "16px",
    backgroundColor: `${color}15`,
    borderLeft: `4px solid ${color}`,
    borderRadius: "12px",
    fontSize: "14px",
    color: "#374151",
    lineHeight: "1.6",
    fontStyle: isItalic ? "italic" : "normal",
  });

  const promptCardStyle = (isHovered: boolean): React.CSSProperties => ({
    width: "100%",
    textAlign: "left",
    padding: "12px 16px",
    backgroundColor: "#FFFFFF",
    border: isHovered ? "2px solid #9333EA" : "2px solid #E5E7EB",
    borderRadius: "20px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginBottom: "8px",
    fontFamily: "'Segoe UI', sans-serif",
    outline: "none",
  });

  const promptTextStyle = (isHovered: boolean): React.CSSProperties => ({
    fontSize: "14px",
    color: isHovered ? "#7C3AED" : "#374151",
    lineHeight: "1.5",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "12px",
  });

  const arrowIconStyle = (isHovered: boolean): React.CSSProperties => ({
    opacity: isHovered ? 1 : 0,
    transition: "opacity 0.2s ease",
    color: "#A78BFA",
    fontSize: "16px",
    flexShrink: 0,
  });

  // Placeholder style for missing content
  const placeholderStyle = (color: string): React.CSSProperties => ({
    padding: "16px",
    backgroundColor: `${color}08`,
    border: `1px dashed ${color}40`,
    borderRadius: "8px",
    fontSize: "14px",
    color: "#6B7280",
    lineHeight: "1.6",
    fontStyle: "italic",
  });

  // Loading placeholder style (shown during streaming) - container with gradient border
  const loadingPlaceholderContainerStyle: React.CSSProperties = {
    position: "relative",
    padding: "16px",
    borderRadius: "8px",
    overflow: "hidden",
  };

  // Loading placeholder text style
  const loadingPlaceholderTextStyle = (color: string): React.CSSProperties => ({
    fontSize: "14px",
    color: color,
    lineHeight: "1.6",
    position: "relative",
    zIndex: 1,
  });

  // Render tab content based on active tab (with placeholders for missing content)
  const renderTabContent = () => {
    switch (activeTab) {
      case "nextSteps":
        // Check if we have next steps content
        const hasNextSteps =
          effectiveData.element3 && effectiveData.element3.clickablePrompts.length > 0;

        return (
          <div>
            <div style={elementHeaderStyle("#9333EA")}>
              <NextStepsIcon size={20} color="#9333EA" />
              <h5 style={{ margin: 0, fontSize: "14px" }}>Suggested Next Steps</h5>
            </div>
            {hasNextSteps && effectiveData.element3 ? (
              <div>
                {effectiveData.element3.clickablePrompts.map((prompt) => {
                  const isHovered = hoveredPrompt === prompt.id;
                  return (
                    <button
                      key={prompt.id}
                      onClick={() => handlePromptClick(prompt)}
                      onMouseEnter={() => setHoveredPrompt(prompt.id)}
                      onMouseLeave={() => setHoveredPrompt(null)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handlePromptClick(prompt);
                        }
                      }}
                      style={promptCardStyle(isHovered)}
                      aria-label={`Select prompt: ${prompt.text}`}
                    >
                      <div style={promptTextStyle(isHovered)}>
                        <span style={{ flex: 1 }}>{prompt.text}</span>
                        <span style={arrowIconStyle(isHovered)}>→</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : isStreaming ? (
              <div style={loadingPlaceholderContainerStyle}>
                {/* Animated gradient border layer */}
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "8px",
                    background: "linear-gradient(90deg, #9333EA, #c4b5fd, #9333EA, #c4b5fd)",
                    backgroundSize: "300% 100%",
                    filter: "blur(4px)",
                    animation: "gradientSpin 3s linear infinite",
                    pointerEvents: "none",
                  }}
                />
                {/* Inner white background layer */}
                <span
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: "2px",
                    right: "2px",
                    bottom: "2px",
                    borderRadius: "8px",
                    background: "#ffffff",
                    pointerEvents: "none",
                  }}
                />
                {/* Content text */}
                <span style={loadingPlaceholderTextStyle("#9333EA")}>
                  Generating suggestions...
                </span>
              </div>
            ) : (
              <div style={placeholderStyle("#9333EA")}>
                This analysis didn't include suggested next steps. The AI may have focused on
                providing a comprehensive answer to your immediate question instead.
              </div>
            )}
            {pinnedAction && <div style={{ marginTop: "8px" }}>{pinnedAction}</div>}
          </div>
        );

      case "strategic":
        // Check if we have strategic content
        const hasStrategic =
          effectiveData.element1 && effectiveData.element1.content.trim().length > 0;

        return (
          <div>
            <div style={elementHeaderStyle("#0066cc")}>
              <StrategicQuestionIcon size={16} color="#0066cc" />
              <h5 style={{ margin: 0, fontSize: "14px" }}>
                {effectiveData.element1?.label || "Strategic Question"}
              </h5>
            </div>
            {hasStrategic && effectiveData.element1 ? (
              <div style={contentBoxStyle("#0066cc", true)}>
                <p style={{ margin: 0 }}>{effectiveData.element1.content}</p>
              </div>
            ) : isStreaming ? (
              <div style={loadingPlaceholderContainerStyle}>
                {/* Animated gradient border layer */}
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "8px",
                    background: "linear-gradient(90deg, #0066cc, #75B6FF, #0066cc, #75B6FF)",
                    backgroundSize: "300% 100%",
                    filter: "blur(4px)",
                    animation: "gradientSpin 3s linear infinite",
                    pointerEvents: "none",
                  }}
                />
                {/* Inner white background layer */}
                <span
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: "2px",
                    right: "2px",
                    bottom: "2px",
                    borderRadius: "8px",
                    background: "#ffffff",
                    pointerEvents: "none",
                  }}
                />
                {/* Content text */}
                <span style={loadingPlaceholderTextStyle("#0066cc")}>
                  Analyzing strategic implications...
                </span>
              </div>
            ) : (
              <div style={placeholderStyle("#0066cc")}>
                This analysis didn't include strategic insights. This may be a straightforward
                question where deeper strategic considerations weren't applicable.
              </div>
            )}
          </div>
        );

      case "enhancement":
        // Check if we have enhancement content
        const hasEnhancement =
          effectiveData.element2 && effectiveData.element2.content.trim().length > 0;

        return (
          <div>
            <div style={elementHeaderStyle("#059669")}>
              <PromptEnhancementIcon size={16} color="#059669" />
              <h5 style={{ margin: 0, fontSize: "14px" }}>
                {effectiveData.element2?.label || "Input Optimization"}
              </h5>
            </div>
            {hasEnhancement && effectiveData.element2 ? (
              <div style={contentBoxStyle("#00cc66", false)}>
                <p style={{ margin: 0 }}>{effectiveData.element2.content}</p>
              </div>
            ) : isStreaming ? (
              <div style={loadingPlaceholderContainerStyle}>
                {/* Animated gradient border layer */}
                <span
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: "8px",
                    background: "linear-gradient(90deg, #00cc66, #66ffaa, #00cc66, #66ffaa)",
                    backgroundSize: "300% 100%",
                    filter: "blur(4px)",
                    animation: "gradientSpin 3s linear infinite",
                    pointerEvents: "none",
                  }}
                />
                {/* Inner white background layer */}
                <span
                  style={{
                    position: "absolute",
                    top: "2px",
                    left: "2px",
                    right: "2px",
                    bottom: "2px",
                    borderRadius: "8px",
                    background: "#ffffff",
                    pointerEvents: "none",
                  }}
                />
                {/* Content text */}
                <span style={loadingPlaceholderTextStyle("#00cc66")}>
                  Crafting optimization suggestions...
                </span>
              </div>
            ) : (
              <div style={placeholderStyle("#00cc66")}>
                This analysis didn't include input optimization suggestions. Your prompt may have
                already been clear and complete, requiring no additional refinement.
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={containerStyle} className={className}>
      <h4 style={titleStyle}>Prompt Coach</h4>

      {/* Tab Navigation */}
      <div role="tablist" aria-label="Prompt Coach sections" style={tabListStyle}>
        {allTabs.map((tab, index) => {
          const isActive = activeTab === tab.id;
          const isHovered = hoveredTab === tab.id;
          const IconComponent = tab.IconComponent;
          const textColor = tab.textColor || tab.color; // Use textColor if provided, else use color
          const iconColor = isActive ? textColor : "#6B7280"; // Active color or gray

          // Generate animated gradient for each tab color
          const getLightColor = (color: string) => {
            if (color === "#9333EA") return "#c4b5fd"; // Purple light
            if (color === "#0066cc") return "#75B6FF"; // Blue light
            if (color === "#00cc66") return "#66ffaa"; // Green light
            return color;
          };

          const animatedGradient = `linear-gradient(90deg, ${tab.color}, ${getLightColor(tab.color)}, ${tab.color}, ${getLightColor(tab.color)})`;

          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`panel-${tab.id}`}
              id={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              onMouseEnter={() => setHoveredTab(tab.id)}
              onMouseLeave={() => setHoveredTab(null)}
              onKeyDown={(e) => handleTabKeyDown(e, tab.id, index)}
              style={{
                ...getTabButtonStyle(tab, isActive, isHovered),
                color: isActive ? textColor : "#6B7280", // Use textColor for active state
              }}
            >
              {/* Animated gradient border layer (visible when active) */}
              <span
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: "50px",
                  background: animatedGradient,
                  backgroundSize: "300% 100%",
                  opacity: isActive ? 1 : 0,
                  filter: "blur(4px)",
                  transition: "opacity 300ms ease-out",
                  animation: isActive ? "gradientSpin 3s linear infinite" : "none",
                  pointerEvents: "none",
                }}
              />

              {/* Inner background layer */}
              <span
                style={{
                  position: "absolute",
                  top: "2px",
                  left: "2px",
                  right: "2px",
                  bottom: "2px",
                  borderRadius: "50px",
                  background: "#ffffff",
                  pointerEvents: "none",
                }}
              />

              {/* Content layer */}
              <span
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <IconComponent size={tab.iconSize} color={iconColor} />
                <span>{tab.label}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Gradient animation keyframes */}
      <style>{`
        @keyframes gradientSpin {
          0% {
            background-position: 0% 50%;
          }
          100% {
            background-position: 300% 50%;
          }
        }
      `}</style>

      {/* Tab Content */}
      <div
        role="tabpanel"
        id={`panel-${activeTab}`}
        aria-labelledby={`tab-${activeTab}`}
        style={contentAreaStyle}
      >
        {renderTabContent()}
      </div>
    </div>
  );
};

export default PromptCoachPanel;
